import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PhoneCall, ArrowRight, Loader2, Phone } from "lucide-react";
import { Reveal, FadeIn, Overline } from "@/components/Reveal";
import { Field, inputProps } from "@/components/Field";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const steps = [
  {
    num: "01",
    title: "We reach out within 24 hours",
    text: "A friendly care coordinator calls to learn about your situation and answer your first questions.",
  },
  {
    num: "02",
    title: "Free care consultation",
    text: "We meet you — at home or by phone — to understand needs, routines, preferences, and goals.",
  },
  {
    num: "03",
    title: "A personalized care plan",
    text: "Together we design a flexible plan and match you with the right caregiver or nurse.",
  },
  {
    num: "04",
    title: "Care begins — and so does support",
    text: "Care starts on your schedule, with RN oversight and 24/7 on-call support for your family.",
  },
];

const RequestCare = () => {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", relationship: "",
    care_type: "", location: "", preferred_contact: "Phone", message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/care-requests`, form);
      toast.success("Request received! Our care team will contact you within 24 hours.");
      setForm({ name: "", email: "", phone: "", relationship: "", care_type: "", location: "", preferred_contact: "Phone", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main>
      <section data-testid="request-care-hero" className="relative overflow-hidden bg-blush-50 pb-14 pt-40">
        <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full border border-gold/25" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn><Overline>Request Care</Overline></FadeIn>
          <FadeIn delay={0.15}>
            <h1 className="mt-5 max-w-3xl font-serif text-4xl font-medium tracking-tight text-charcoal sm:text-5xl lg:text-6xl">
              Ready for a <em className="text-gold-deep">New Beginning?</em>
            </h1>
          </FadeIn>
          <FadeIn delay={0.3}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-charcoal-muted sm:text-lg">
              Our compassionate team is here to help you understand your options
              and find the care that's right for you or your loved one.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="bg-white py-20 sm:py-28">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-[1.2fr_1fr]">
          <Reveal>
            <form
              onSubmit={submit}
              data-testid="request-care-form"
              className="rounded-[2rem] border border-blush-200 bg-white p-7 shadow-[0_10px_44px_rgba(31,41,55,0.07)] sm:p-10"
            >
              <h2 className="font-serif text-2xl text-charcoal">Request a free care consultation</h2>
              <p className="mt-2 text-sm text-charcoal-muted">
                No obligation. We typically respond within one business day.
              </p>
              <div className="mt-8 grid gap-5 sm:grid-cols-2">
                <Field label="Full name" htmlFor="rc-name" required>
                  <input id="rc-name" name="name" required value={form.name} onChange={update} placeholder="Your name" {...inputProps("request-care-form-name-input")} />
                </Field>
                <Field label="Email" htmlFor="rc-email" required>
                  <input id="rc-email" name="email" type="email" required value={form.email} onChange={update} placeholder="you@email.com" {...inputProps("request-care-form-email-input")} />
                </Field>
                <Field label="Phone" htmlFor="rc-phone" required>
                  <input id="rc-phone" name="phone" type="tel" required value={form.phone} onChange={update} placeholder="(732) 555-0100" {...inputProps("request-care-form-phone-input")} />
                </Field>
                <Field label="Who is care for?" htmlFor="rc-relationship" required>
                  <select id="rc-relationship" name="relationship" required value={form.relationship} onChange={update} {...inputProps("request-care-form-relationship-select")}>
                    <option value="" disabled>Select one</option>
                    {["Myself", "Parent", "Spouse", "Other family member", "Client / Patient", "Friend"].map((x) => <option key={x} value={x}>{x}</option>)}
                  </select>
                </Field>
                <Field label="Type of care needed" htmlFor="rc-care-type" required>
                  <select id="rc-care-type" name="care_type" required value={form.care_type} onChange={update} {...inputProps("request-care-form-care-type-select")}>
                    <option value="" disabled>Select a service</option>
                    {["Personal Care Assistance", "Home Health Aide Services", "Skilled Nursing", "Private Duty Nursing", "Companion & Support Services", "Family & Caregiver Support", "Not sure yet"].map((x) => <option key={x} value={x}>{x}</option>)}
                  </select>
                </Field>
                <Field label="City / County in NJ" htmlFor="rc-location">
                  <input id="rc-location" name="location" value={form.location} onChange={update} placeholder="e.g. Edison, Middlesex County" {...inputProps("request-care-form-location-input")} />
                </Field>
                <div className="sm:col-span-2">
                  <span className="field-label">Preferred contact method</span>
                  <div className="flex gap-3">
                    {["Phone", "Email"].map((m) => (
                      <button
                        key={m}
                        type="button"
                        data-testid={`request-care-contact-${m.toLowerCase()}`}
                        onClick={() => setForm({ ...form, preferred_contact: m })}
                        className={`flex-1 rounded-xl border px-4 py-3 text-sm font-medium transition-[background-color,border-color,color] duration-200 ${
                          form.preferred_contact === m
                            ? "border-gold bg-gold-soft text-gold-deep"
                            : "border-blush-300/70 bg-white text-charcoal-muted hover:border-blush-400"
                        }`}
                      >
                        {m}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <Field label="Tell us about your situation" htmlFor="rc-message">
                    <textarea id="rc-message" name="message" rows={4} value={form.message} onChange={update} placeholder="Share anything that helps us understand your loved one's needs..." {...inputProps("request-care-form-message-textarea")} />
                  </Field>
                </div>
              </div>
              <button
                type="submit"
                data-testid="request-care-form-submit-button"
                disabled={loading}
                className="btn-pink mt-8 w-full disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                {loading ? "Sending..." : "Request a Care Consultation"}
              </button>
            </form>
          </Reveal>

          <div>
            <Reveal delay={0.1}>
              <Overline>What Happens Next</Overline>
              <h2 className="mt-5 font-serif text-3xl font-medium tracking-tight text-charcoal sm:text-4xl">
                Four gentle steps to care
              </h2>
            </Reveal>
            <div className="mt-10 space-y-0">
              {steps.map((s, i) => (
                <Reveal key={s.num} delay={i * 0.1}>
                  <div className="relative flex gap-6 pb-10">
                    {i < steps.length - 1 && (
                      <span className="absolute left-[26px] top-14 h-[calc(100%-3.5rem)] w-px bg-gradient-to-b from-gold/40 to-blush-300" aria-hidden="true" />
                    )}
                    <span className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-full border border-gold/40 bg-white font-serif text-lg italic text-gold-deep shadow-sm">
                      {s.num}
                    </span>
                    <div className="pt-1.5">
                      <h3 className="font-serif text-xl text-charcoal">{s.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-charcoal-muted">{s.text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.2}>
              <div className="mt-4 rounded-2xl border border-gold/30 bg-gold-soft/60 p-6">
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-sm">
                    <PhoneCall className="h-5 w-5 text-gold" strokeWidth={1.5} />
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-charcoal-muted">Prefer to talk now?</p>
                    <a href="tel:+18565457393" data-testid="request-care-hotline" className="font-serif text-xl text-charcoal transition-colors hover:text-gold-deep">
                      (856) 545-7393
                    </a>
                  </div>
                </div>
                <p className="mt-3 text-xs leading-relaxed text-charcoal-muted">
                  For urgent medical needs, please call 911 first. Our care line
                  is answered 24/7 for non-emergency questions.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
};

export default RequestCare;
